import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/libs/utils";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { buildUserAccessBatchEndpoints } from "@/libs/subcontractorAccess";
import {
  ArrowUpRight,
  BellRing,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  FileText,
  FolderKanban,
  Landmark,
  Search,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  buildCalendarEvents,
  buildReminders,
} from "./SynthesesFinanceursReadOnlyBoard";
import {
  buildProjectCatalog,
  buildProjectReminders,
  buildProjectsFromContracts,
} from "./SynthesesProjetsBoard";
import { buildSubcontractors } from "./SynthesesIntervenantsBoard";
import SynthesesShell from "./SynthesesShell";
import type {
  SynthesesCalendarEvent,
  SynthesesReminderItem,
} from "./SynthesesRightRail";
import useSynthesesGroupedContractsRead from "./useSynthesesGroupedContractsRead";

type ProgramResponse = Array<{
  id?: number;
  name?: string;
  projects?: Array<{
    id?: number;
    name?: string;
  }>;
}>;

type DashboardEventKind = "contract" | "financeur" | "terrain";

type DashboardReminder = SynthesesReminderItem & {
  scopeId?: number | null;
  scopeLabel: string;
  route: string;
};

type DashboardEventItem = SynthesesCalendarEvent & {
  scopeId: number | null;
  scopeLabel: string;
  route: string;
  dateValue: Date;
  dateKey: string;
  kind: DashboardEventKind;
};

type CalendarCell = {
  date: Date | null;
  day: number | null;
};

const REMINDER_LEVEL_ORDER = {
  high: 1,
  medium: 2,
  low: 3,
} satisfies Record<SynthesesReminderItem["level"], number>;

const EVENT_KIND_CONFIG: Record<
  DashboardEventKind,
  {
    label: string;
    dotColor: string;
    dotClassName: string;
    pillClassName: string;
    rowClassName: string;
  }
> = {
  contract: {
    label: "Contrat signé",
    dotColor: "#10b981",
    dotClassName: "bg-emerald-500",
    pillClassName: "border-emerald-200 bg-emerald-50 text-emerald-700",
    rowClassName: "border-emerald-200 bg-emerald-50/55",
  },
  financeur: {
    label: "Versement financeur",
    dotColor: "#d97706",
    dotClassName: "bg-amber-600",
    pillClassName: "border-amber-200 bg-amber-50 text-amber-700",
    rowClassName: "border-amber-200 bg-amber-50/55",
  },
  terrain: {
    label: "Versement à l'intervenant",
    dotColor: "#e11d48",
    dotClassName: "bg-rose-600",
    pillClassName: "border-rose-200 bg-rose-50 text-rose-700",
    rowClassName: "border-rose-200 bg-rose-50/55",
  },
};

const REMINDER_LEVEL_CONFIG = {
  high: {
    label: "Important",
    pillClassName: "border-rose-200 bg-rose-50 text-rose-700",
    rowClassName: "border-rose-200 bg-rose-50/60",
  },
  medium: {
    label: "À faire",
    pillClassName: "border-amber-200 bg-amber-50 text-amber-700",
    rowClassName: "border-amber-200 bg-amber-50/60",
  },
  low: {
    label: "Info",
    pillClassName: "border-slate-200 bg-slate-50 text-slate-700",
    rowClassName: "border-slate-200 bg-slate-50/75",
  },
} satisfies Record<
  SynthesesReminderItem["level"],
  {
    label: string;
    pillClassName: string;
    rowClassName: string;
  }
>;

const URGENT_BADGE_CLASS =
  "rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700";
const COMPLETED_BADGE_CLASS =
  "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700";
const FINANCEUR_NAVIGATION_TARGET_STORAGE_KEY =
  "syntheses:financeurs:navigation-target";

function toTrimmedText(value: unknown) {
  return String(value ?? "").trim();
}

function parseDateOrNull(value: string | Date | null | undefined) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateKey(value: string | Date | null | undefined) {
  const date = parseDateOrNull(value);
  if (!date) return null;
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildMonthCells(cursor: Date): CalendarCell[] {
  const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const daysInMonth = new Date(
    cursor.getFullYear(),
    cursor.getMonth() + 1,
    0,
  ).getDate();
  const firstWeekdayMonday0 = (firstDay.getDay() + 6) % 7;
  const totalCells = Math.ceil((firstWeekdayMonday0 + daysInMonth) / 7) * 7;

  const cells: CalendarCell[] = [];
  for (let index = 0; index < totalCells; index += 1) {
    const day = index - firstWeekdayMonday0 + 1;
    if (day < 1 || day > daysInMonth) {
      cells.push({ date: null, day: null });
      continue;
    }

    cells.push({
      date: new Date(cursor.getFullYear(), cursor.getMonth(), day),
      day,
    });
  }

  return cells;
}

function formatDateLabel(value: string | Date | null | undefined) {
  const date = parseDateOrNull(value);
  if (!date) return "Date invalide";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatMonthLabel(value: Date) {
  const raw = new Intl.DateTimeFormat("fr-FR", {
    month: "long",
    year: "numeric",
  }).format(value);
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function formatDayTitle(value: string | Date | null | undefined) {
  const date = parseDateOrNull(value);
  if (!date) return "Jour sélectionné";
  const raw = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function startOfLocalDay(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function getEventUrgencyState(
  event: Pick<DashboardEventItem, "dateValue" | "isPending">,
) {
  if (event.isPending !== true) return null;

  const todayStart = startOfLocalDay(new Date());
  const targetStart = startOfLocalDay(event.dateValue);
  const diffDays = Math.round(
    (targetStart.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays < 0) return "urgent";
  if (diffDays <= 7) return "urgent";
  return null;
}

function getEventUrgencyDetail(
  event: Pick<DashboardEventItem, "dateValue" | "isPending" | "kind">,
) {
  if (event.isPending !== true) return null;

  const todayStart = startOfLocalDay(new Date());
  const targetStart = startOfLocalDay(event.dateValue);
  const diffDays = Math.round(
    (targetStart.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays < 0) {
    return `${EVENT_KIND_CONFIG[event.kind].label} en attente depuis le ${formatDateLabel(event.dateValue)}.`;
  }

  if (diffDays <= 7) {
    return `${EVENT_KIND_CONFIG[event.kind].label} prévu pour le ${formatDateLabel(event.dateValue)} et à traiter dans moins d'une semaine.`;
  }

  return null;
}

function getEventCompletionLabel(
  event: Pick<DashboardEventItem, "isPending" | "completionLabel">,
) {
  if (event.isPending === true) return null;
  return toTrimmedText(event.completionLabel) || null;
}

function getEventCompletionDetail(
  event: Pick<DashboardEventItem, "kind" | "dateValue" | "completionMedia">,
  completionLabel: string | null,
): ReactNode {
  if (!completionLabel) return null;

  if (event.kind === "contract" && completionLabel === "Signé") {
    return `Ce contrat est déjà signé. La date affichée correspond à la date de signature: ${formatDateLabel(event.dateValue)}.`;
  }

  if (event.kind === "financeur" && completionLabel === "Reçu") {
    return `Ce versement financeur est déjà reçu. Date affichée: ${formatDateLabel(event.dateValue)}.`;
  }

  if (event.kind === "terrain" && completionLabel === "Réalisé") {
    return `Ce versement à l'intervenant est déjà réalisé. La date affichée correspond à la date de réalisation: ${formatDateLabel(event.dateValue)}.`;
  }

  if (event.kind === "terrain" && completionLabel === "Certifié") {
    const mediaLabel = toTrimmedText(event.completionMedia?.label);
    const mediaHref = toTrimmedText(event.completionMedia?.href);

    return (
      <div className="space-y-2">
        <div>
          Ce versement à l'intervenant est déjà certifié. La date affichée
          correspond à la date de certification: {formatDateLabel(event.dateValue)}.
        </div>
        {mediaLabel ? (
          <div className="space-y-1">
            <div className="font-medium text-slate-900">Pièce jointe</div>
            {mediaHref ? (
              <a
                href={mediaHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sky-700 underline underline-offset-2 hover:text-sky-800"
              >
                {mediaLabel}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            ) : (
              <div>{mediaLabel}</div>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  return `${completionLabel} le ${formatDateLabel(event.dateValue)}.`;
}

function getEventCompletionBadgeClass(
  event: Pick<DashboardEventItem, "kind">,
  completionLabel: string | null,
) {
  if (event.kind === "terrain" && completionLabel === "Réalisé") {
    return "rounded-full border border-verdeogreen/20 bg-verdeogreen/10 px-2 py-0.5 text-[10px] font-semibold text-verdeogreen";
  }

  if (event.kind === "terrain" && completionLabel === "Certifié") {
    return "rounded-full border border-verdeoorange/20 bg-verdeoorange/10 px-2 py-0.5 text-[10px] font-semibold text-verdeoorange";
  }

  return COMPLETED_BADGE_CLASS;
}

function shouldShowCompletionBadgeInDetailedList(
  event: Pick<DashboardEventItem, "kind">,
  completionLabel: string | null,
) {
  if (event.kind === "financeur" && completionLabel === "Reçu") {
    return false;
  }

  return true;
}

function getReminderUrgencyDetail() {
  return "Ce rappel est prioritaire et doit être traité rapidement.";
}

function DashboardBadge({
  label,
  className,
  detail,
}: {
  label: string;
  className: string;
  detail?: ReactNode;
}) {
  const hasDetail =
    typeof detail === "string" ? !!toTrimmedText(detail) : detail != null;

  if (!hasDetail) {
    return <span className={className}>{label}</span>;
  }

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn(className, "cursor-help")}>{label}</span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[280px] text-left text-xs leading-relaxed"
        >
          {detail}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function getEventKind(label: string): DashboardEventKind {
  const normalizedLabel = toTrimmedText(label).toLowerCase();
  if (normalizedLabel.includes("terrain") || normalizedLabel.includes("intervenant")) {
    return "terrain";
  }
  if (
    normalizedLabel.includes("financeur")
    || normalizedLabel.includes("reçu")
    || normalizedLabel.includes("recu")
    || normalizedLabel.includes("prévu")
    || normalizedLabel.includes("prevu")
  ) {
    return "financeur";
  }
  return "contract";
}

function getInitialSelectedDateKey(events: DashboardEventItem[]) {
  const today = new Date();
  const todayKey = toDateKey(today);

  if (todayKey && events.some((event) => event.dateKey === todayKey)) {
    return todayKey;
  }

  const upcomingEvent = events.find((event) => event.dateValue >= today);
  if (upcomingEvent) return upcomingEvent.dateKey;

  return events[0]?.dateKey || todayKey || null;
}

function SynthesesDashboardTabsPrefetcher() {
  const apiBase = useMemo(
    () =>
      String(import.meta.env.VITE_API_ENDPOINT || "")
        .trim()
        .replace(/\/+$/, ""),
    [],
  );

  const financeursEndpoint = apiBase ? `${apiBase}/financers` : "";
  const subcontractorsEndpoint = apiBase ? `${apiBase}/users/subcontractors` : "";

  useFetchDataV2<any[]>(financeursEndpoint, {
    browserCache: true,
    browserCacheNamespace: "syntheses",
  });
  const { data: subcontractorsData } = useFetchDataV2<any>(
    subcontractorsEndpoint,
    { browserCache: true, browserCacheNamespace: "syntheses" },
  );
  const { prefetchData } = useFetchDataV2<any>("", {
    browserCache: true,
    browserCacheNamespace: "syntheses",
  });
  const defaultSubcontractor = useMemo(
    () => buildSubcontractors(subcontractorsData)[0] || null,
    [subcontractorsData],
  );

  useEffect(() => {
    if (!apiBase || !defaultSubcontractor?.memberIds?.length) return;

    const endpoints = buildUserAccessBatchEndpoints(
      apiBase,
      defaultSubcontractor.memberIds,
    );
    if (endpoints.length === 0) return;

    void Promise.all(endpoints.map((endpoint) => prefetchData(endpoint)));
  }, [apiBase, defaultSubcontractor, prefetchData]);

  return null;
}

function SynthesesTableauDeBord() {
  const navigate = useNavigate();
  const groupedContractsQuery = useSynthesesGroupedContractsRead("financeurs");
  const programsQuery = useFetchDataV2<ProgramResponse>(
    `${import.meta.env.VITE_API_ENDPOINT}/info/programs`,
    { browserCache: true, browserCacheNamespace: "syntheses" },
  );

  const groupedFinanceurs = useMemo(
    () =>
      Array.isArray(groupedContractsQuery.data?.items)
        ? groupedContractsQuery.data.items
        : [],
    [groupedContractsQuery.data?.items],
  );
  const projectCatalog = useMemo(
    () => buildProjectCatalog(programsQuery.data),
    [programsQuery.data],
  );
  const projects = useMemo(
    () => buildProjectsFromContracts(projectCatalog, groupedFinanceurs),
    [groupedFinanceurs, projectCatalog],
  );

  const navigateToTarget = ({
    route,
    scopeId,
    scopeLabel,
    targetNodeId,
    targetSynthesesContractId,
  }: {
    route: string;
    scopeId?: number | null;
    scopeLabel?: string | null;
    targetNodeId?: string | null;
    targetSynthesesContractId?: number | null;
  }) => {
    if (
      route !== "/dashboard/syntheses/financeurs"
      || (
        !toTrimmedText(targetNodeId)
        && !Number.isFinite(Number(targetSynthesesContractId))
      )
    ) {
      navigate(route);
      return;
    }

    const financeurTarget = {
      financeurId:
        Number.isFinite(Number(scopeId)) && Number(scopeId) > 0
          ? String(Number(scopeId))
          : null,
      financeurName: toTrimmedText(scopeLabel) || null,
      contractNodeId: toTrimmedText(targetNodeId) || null,
      synthesesContractId:
        Number.isFinite(Number(targetSynthesesContractId))
        && Number(targetSynthesesContractId) > 0
          ? Number(targetSynthesesContractId)
          : null,
    };

    const searchParams = new URLSearchParams();
    if (Number.isFinite(Number(scopeId)) && Number(scopeId) > 0) {
      searchParams.set("targetFinanceurId", String(Number(scopeId)));
    }
    if (toTrimmedText(scopeLabel)) {
      searchParams.set("targetFinanceurName", String(scopeLabel));
    }
    if (toTrimmedText(targetNodeId)) {
      searchParams.set("targetContractNodeId", String(targetNodeId));
    }
    if (
      Number.isFinite(Number(targetSynthesesContractId))
      && Number(targetSynthesesContractId) > 0
    ) {
      searchParams.set(
        "targetSynthesesContractId",
        String(Number(targetSynthesesContractId)),
      );
    }

    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(
        FINANCEUR_NAVIGATION_TARGET_STORAGE_KEY,
        JSON.stringify(financeurTarget),
      );
    }

    navigate({
      pathname: route,
      search: `?${searchParams.toString()}`,
    }, {
      state: {
        synthesesFinanceurTarget: financeurTarget,
      },
    });
  };

  const reminders = useMemo<DashboardReminder[]>(
    () =>
      [
        ...groupedFinanceurs.flatMap((financeur, index) =>
          buildReminders(financeur).map((reminder) => ({
            ...reminder,
            id: `financeur-${financeur.id ?? index}-${reminder.id}`,
            scopeId:
              Number.isFinite(Number(financeur.id)) && Number(financeur.id) > 0
                ? Number(financeur.id)
                : null,
            scopeLabel: financeur.name,
            route: "/dashboard/syntheses/financeurs",
          })),
        ),
        ...projects.flatMap((project) =>
          buildProjectReminders(project).map((reminder) => ({
            ...reminder,
            id: `project-${project.id}-${reminder.id}`,
            scopeLabel: project.name,
            route: "/dashboard/syntheses/projets",
          })),
        ),
      ].sort((left, right) => {
        const levelDiff =
          REMINDER_LEVEL_ORDER[left.level] - REMINDER_LEVEL_ORDER[right.level];
        if (levelDiff !== 0) return levelDiff;
        return left.title.localeCompare(right.title, "fr");
      }),
    [groupedFinanceurs, projects],
  );

  const events = useMemo<DashboardEventItem[]>(
    () =>
      groupedFinanceurs
        .flatMap((financeur, index) =>
          buildCalendarEvents(financeur).map((event) => {
            const dateValue = parseDateOrNull(event.date);
            const dateKey = toDateKey(dateValue);
            if (!dateValue || !dateKey) return null;

            return {
              ...event,
              id: `financeur-${financeur.id ?? index}-${event.id}`,
              scopeId:
                Number.isFinite(Number(financeur.id)) && Number(financeur.id) > 0
                  ? Number(financeur.id)
                  : null,
              scopeLabel: financeur.name,
              route: "/dashboard/syntheses/financeurs",
              dateValue,
              dateKey,
              kind: getEventKind(event.label),
            } satisfies DashboardEventItem;
          }),
        )
        .filter(Boolean)
        .sort((left, right) => left.dateValue.getTime() - right.dateValue.getTime()) as DashboardEventItem[],
    [groupedFinanceurs],
  );

  const [searchValue, setSearchValue] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<"all" | DashboardEventKind>("all");
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [calendarCursor, setCalendarCursor] = useState(() => new Date());

  const handleOpenEvent = (event: DashboardEventItem) => {
    navigateToTarget({
      route: event.route,
      scopeId: event.scopeId,
      scopeLabel: event.scopeLabel,
      targetNodeId: event.targetNodeId,
      targetSynthesesContractId: event.targetSynthesesContractId,
    });
  };

  const handleOpenReminder = (reminder: DashboardReminder) => {
    navigateToTarget({
      route: reminder.route,
      scopeId: reminder.scopeId,
      scopeLabel: reminder.scopeLabel,
      targetNodeId: reminder.targetNodeId,
      targetSynthesesContractId: reminder.targetSynthesesContractId,
    });
  };

  useEffect(() => {
    const initialDateKey = getInitialSelectedDateKey(events);
    setSelectedDateKey((current) => current || initialDateKey);

    const initialDate = parseDateOrNull(initialDateKey);
    if (!initialDate) return;

    setCalendarCursor((current) => {
      if (
        current.getFullYear() === initialDate.getFullYear()
        && current.getMonth() === initialDate.getMonth()
      ) {
        return current;
      }
      return new Date(initialDate.getFullYear(), initialDate.getMonth(), 1);
    });
  }, [events]);

  const eventsByDateKey = useMemo(() => {
    const map = new Map<string, DashboardEventItem[]>();
    events.forEach((event) => {
      const current = map.get(event.dateKey) || [];
      current.push(event);
      map.set(event.dateKey, current);
    });
    return map;
  }, [events]);

  const selectedDateEvents = useMemo(
    () => (selectedDateKey ? eventsByDateKey.get(selectedDateKey) || [] : []),
    [eventsByDateKey, selectedDateKey],
  );

  const visibleReminders = useMemo(
    () => reminders.filter((reminder) => reminder.level !== "low").slice(0, 4),
    [reminders],
  );

  const filteredEvents = useMemo(() => {
    const normalizedSearchValue = toTrimmedText(searchValue).toLowerCase();

    return events.filter((event) => {
      if (selectedFilter !== "all" && event.kind !== selectedFilter) return false;
      if (!normalizedSearchValue) return true;

      const haystack = [
        event.label,
        event.scopeLabel,
        EVENT_KIND_CONFIG[event.kind].label,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearchValue);
    });
  }, [events, searchValue, selectedFilter]);

  const calendarCells = useMemo(
    () => buildMonthCells(calendarCursor),
    [calendarCursor],
  );

  const loading = groupedContractsQuery.loading || programsQuery.loading;
  const hasError = groupedContractsQuery.error || programsQuery.error;

  return (
    <SynthesesShell
      active="dashboard"
      title="Suivi de financements • Tableau de bord"
      description="Vision rapide des échéances, actions à traiter et contrats suivis."
    >
      <SynthesesDashboardTabsPrefetcher />

      {hasError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          Impossible de charger le tableau de bord synthèses.
        </div>
      ) : null}

      {loading ? (
        <div className="space-y-4">
          <div className="overflow-x-auto pb-1">
            <div
              className="grid items-start gap-4"
              style={{
                minWidth: "1040px",
                gridTemplateColumns: "360px minmax(0, 1fr)",
              }}
            >
              <Skeleton className="h-[380px] rounded-[26px]" />
              <Skeleton className="h-[380px] rounded-[26px]" />
            </div>
          </div>
          <Skeleton className="h-[420px] rounded-[26px]" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">
              <Landmark className="h-3.5 w-3.5 text-emerald-600" />
              {groupedFinanceurs.length} financeur{groupedFinanceurs.length > 1 ? "s" : ""}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">
              <FileText className="h-3.5 w-3.5 text-sky-600" />
              {events.length} échéance{events.length > 1 ? "s" : ""}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">
              <BellRing className="h-3.5 w-3.5 text-amber-600" />
              {reminders.length} rappel{reminders.length > 1 ? "s" : ""}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">
              <FolderKanban className="h-3.5 w-3.5 text-rose-600" />
              {projects.filter((project) => project.contracts.length > 0).length} projet
              {projects.filter((project) => project.contracts.length > 0).length > 1 ? "s" : ""} suivi
              {projects.filter((project) => project.contracts.length > 0).length > 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto pb-1">
            <div
              className="grid items-start gap-4"
              style={{
                minWidth: "980px",
                gridTemplateColumns: "300px minmax(0, 1fr)",
              }}
            >
            <section className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.03)]">
              <div className="p-5">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      Vue calendrier
                    </div>
                    <h2 className="mt-1 text-[24px] font-semibold text-slate-900">
                      Échéances par date
                    </h2>
                  </div>
                </div>

                <div className="mb-5 flex items-center justify-between">
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
                    onClick={() =>
                      setCalendarCursor((current) =>
                        new Date(current.getFullYear(), current.getMonth() - 1, 1),
                      )
                    }
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="text-sm font-semibold capitalize text-slate-800">
                    {formatMonthLabel(calendarCursor)}
                  </div>
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
                    onClick={() =>
                      setCalendarCursor((current) =>
                        new Date(current.getFullYear(), current.getMonth() + 1, 1),
                      )
                    }
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] font-medium lowercase text-slate-400">
                  {["lu", "ma", "me", "je", "ve", "sa", "di"].map((day) => (
                    <div key={day} className="py-1">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="mt-2 grid grid-cols-7 gap-1.5">
                  {calendarCells.map((cell, index) => {
                    if (!cell.date || !cell.day) {
                      return (
                        <div
                          key={`calendar-empty-${index}`}
                          className="h-12 rounded-xl bg-transparent"
                        />
                      );
                    }

                    const dateKey = toDateKey(cell.date);
                    const dayEvents = dateKey ? eventsByDateKey.get(dateKey) || [] : [];
                    const dayMarkerKinds = (
                      Object.keys(EVENT_KIND_CONFIG) as DashboardEventKind[]
                    ).filter((kind) => dayEvents.some((event) => event.kind === kind));
                    const isSelected = !!dateKey && selectedDateKey === dateKey;

                    return (
                      <button
                        key={dateKey || `calendar-day-${index}`}
                        type="button"
                        onClick={() => setSelectedDateKey(dateKey)}
                        className={cn(
                          "flex h-12 flex-col items-center justify-center rounded-xl px-1 py-1 text-xs transition",
                          isSelected
                            ? "border border-emerald-300 bg-emerald-50 text-slate-700 shadow-sm"
                            : "border border-transparent bg-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50",
                        )}
                      >
                        <span className="font-medium text-slate-700">
                          {cell.day}
                        </span>
                        <span className="mt-1 flex min-h-[16px] w-[28px] flex-wrap items-center justify-center gap-1">
                          {dayMarkerKinds.map((kind, markerIndex) => (
                            <span
                              key={`${dateKey}-${kind}-${markerIndex}`}
                              className="h-1.5 w-1.5 rounded-full"
                              style={{
                                backgroundColor: EVENT_KIND_CONFIG[kind].dotColor,
                              }}
                            />
                          ))}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50/80 p-3">
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    Légende
                  </div>
                  <div className="flex flex-col gap-2">
                  {(Object.keys(EVENT_KIND_CONFIG) as DashboardEventKind[]).map((kind) => (
                    <span
                      key={`calendar-legend-${kind}`}
                      className={cn(
                        "inline-flex w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium",
                        EVENT_KIND_CONFIG[kind].pillClassName,
                      )}
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{
                          backgroundColor: EVENT_KIND_CONFIG[kind].dotColor,
                        }}
                      />
                      {EVENT_KIND_CONFIG[kind].label}
                    </span>
                  ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.03)]">
              <div className="p-5">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      Plan d'action
                    </div>
                    <h2 className="mt-1 text-[24px] font-semibold text-slate-900">
                      Actions — {formatDayTitle(selectedDateKey)}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Les échéances du jour sélectionné et les rappels prioritaires à traiter.
                    </p>
                  </div>
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
                    {selectedDateEvents.length + visibleReminders.length} élément
                    {selectedDateEvents.length + visibleReminders.length > 1 ? "s" : ""}
                  </span>
                </div>

                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-sky-600" />
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-sky-700">
                        Échéances du jour
                      </div>
                    </div>

                    {selectedDateEvents.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                        Aucune échéance sur cette date.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {selectedDateEvents.map((event) => {
                          const urgencyState = getEventUrgencyState(event);
                          const urgencyDetail = getEventUrgencyDetail(event);
                          const completionLabel = getEventCompletionLabel(event);
                          const completionDetail = getEventCompletionDetail(
                            event,
                            completionLabel,
                          );

                          return (
                            <div
                              key={`selected-event-${event.id}`}
                              className={cn(
                                "rounded-2xl border px-4 py-3",
                                EVENT_KIND_CONFIG[event.kind].rowClassName,
                              )}
                            >
                              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span
                                    className={cn(
                                      "rounded-full border px-2 py-0.5 text-[10px] font-medium",
                                      EVENT_KIND_CONFIG[event.kind].pillClassName,
                                    )}
                                  >
                                    {EVENT_KIND_CONFIG[event.kind].label}
                                  </span>
                                  {urgencyState ? (
                                    <DashboardBadge
                                      label="Urgent"
                                      className={URGENT_BADGE_CLASS}
                                      detail={urgencyDetail}
                                    />
                                  ) : null}
                                  {completionLabel ? (
                                    <DashboardBadge
                                      label={completionLabel}
                                      className={getEventCompletionBadgeClass(
                                        event,
                                        completionLabel,
                                      )}
                                      detail={completionDetail}
                                    />
                                  ) : null}
                                  <span className="text-[11px] font-medium text-slate-500">
                                    {event.scopeLabel}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleOpenEvent(event)}
                                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                                >
                                  Ouvrir
                                  <ArrowUpRight className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              <div className="text-sm font-semibold text-slate-900">
                                {event.label}
                              </div>
                              <div className="mt-1 text-xs text-slate-500">
                                Échéance prévue le {formatDateLabel(event.dateValue)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <BellRing className="h-4 w-4 text-amber-600" />
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">
                        Rappels prioritaires
                      </div>
                    </div>

                    {visibleReminders.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                        Aucun rappel prioritaire pour le moment.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {visibleReminders.map((reminder) => (
                          <div
                            key={`priority-reminder-${reminder.id}`}
                            className={cn(
                              "rounded-2xl border px-4 py-3",
                              REMINDER_LEVEL_CONFIG[reminder.level].rowClassName,
                            )}
                          >
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className={cn(
                                    "rounded-full border px-2 py-0.5 text-[10px] font-medium",
                                    REMINDER_LEVEL_CONFIG[reminder.level].pillClassName,
                                  )}
                                >
                                  {REMINDER_LEVEL_CONFIG[reminder.level].label}
                                </span>
                                {reminder.level === "high" ? (
                                  <DashboardBadge
                                    label="Urgent"
                                    className={URGENT_BADGE_CLASS}
                                    detail={getReminderUrgencyDetail()}
                                  />
                                ) : null}
                                <span className="text-[11px] font-medium text-slate-500">
                                  {reminder.scopeLabel}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleOpenReminder(reminder)}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                              >
                                Ouvrir
                                <ArrowUpRight className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <div className="text-sm font-semibold text-slate-900">
                              {reminder.title}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              {reminder.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
            </div>
          </div>

          <section className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.03)]">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Suivi détaillé
                  </div>
                  <h2 className="mt-1 text-[24px] font-semibold text-slate-900">
                    Échéances synthèses
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSearchValue("");
                    setSelectedFilter("all");
                  }}
                  className="text-sm font-medium text-slate-500 transition hover:text-slate-900"
                >
                  Réinitialiser le filtre
                </button>
              </div>

              <div className="mt-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <label className="block xl:max-w-[340px] xl:flex-1">
                  <span className="sr-only">Recherche rapide</span>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="search"
                      value={searchValue}
                      onChange={(event) => setSearchValue(event.target.value)}
                      placeholder="Recherche rapide"
                      className="h-10 w-full rounded-full border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white"
                    />
                  </div>
                </label>

                <div className="flex flex-wrap items-center gap-2">
                  {(["all", ...Object.keys(EVENT_KIND_CONFIG)] as Array<"all" | DashboardEventKind>).map(
                    (filterKey) => {
                      const label =
                        filterKey === "all"
                          ? "Toutes"
                          : EVENT_KIND_CONFIG[filterKey].label;
                      const active = selectedFilter === filterKey;

                      return (
                        <button
                          key={`dashboard-filter-${filterKey}`}
                          type="button"
                          onClick={() => setSelectedFilter(filterKey)}
                          className={cn(
                            "rounded-full border px-3 py-1 text-xs font-medium transition",
                            active
                              ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-800",
                          )}
                        >
                          {label}
                        </button>
                      );
                    },
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="min-w-[860px]">
                <div className="grid grid-cols-[160px_minmax(0,1.4fr)_220px_170px_120px] gap-4 border-b border-slate-200 px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  <div>Date</div>
                  <div>Échéance</div>
                  <div>Périmètre</div>
                  <div>Type</div>
                  <div className="text-right">Action rapide</div>
                </div>

                {filteredEvents.length === 0 ? (
                  <div className="px-5 py-10 text-sm text-slate-500">
                    Aucune échéance ne correspond au filtre actuel.
                  </div>
                ) : (
                  filteredEvents.slice(0, 30).map((event) => {
                    const urgencyState = getEventUrgencyState(event);
                    const urgencyDetail = getEventUrgencyDetail(event);
                    const completionLabel = getEventCompletionLabel(event);
                    const completionDetail = getEventCompletionDetail(
                      event,
                      completionLabel,
                    );

                    return (
                      <div
                        key={`dashboard-row-${event.id}`}
                        className="grid grid-cols-[160px_minmax(0,1.4fr)_220px_170px_120px] gap-4 border-b border-slate-100 px-5 py-4 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900">
                            {formatDateLabel(event.dateValue)}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {event.dateKey === selectedDateKey ? "Jour sélectionné" : "Échéance planifiée"}
                          </div>
                        </div>

                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="truncate font-medium text-slate-900">
                              {event.label}
                            </div>
                            {urgencyState ? (
                              <DashboardBadge
                                label="Urgent"
                                className={URGENT_BADGE_CLASS}
                                detail={urgencyDetail}
                              />
                            ) : null}
                            {completionLabel
                            && shouldShowCompletionBadgeInDetailedList(
                              event,
                              completionLabel,
                            ) ? (
                              <DashboardBadge
                                label={completionLabel}
                                className={getEventCompletionBadgeClass(
                                  event,
                                  completionLabel,
                                )}
                                detail={completionDetail}
                              />
                            ) : null}
                          </div>
                        </div>

                        <div className="min-w-0 text-slate-600">
                          {event.scopeLabel}
                        </div>

                        <div>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium",
                              EVENT_KIND_CONFIG[event.kind].pillClassName,
                            )}
                          >
                            <CircleDot className="h-3.5 w-3.5" />
                            {EVENT_KIND_CONFIG[event.kind].label}
                          </span>
                        </div>

                        <div className="text-right">
                          <button
                            type="button"
                            onClick={() => handleOpenEvent(event)}
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100"
                          >
                            Voir
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </SynthesesShell>
  );
}

export default SynthesesTableauDeBord;
